import { useQuery } from '@tanstack/react-query';

import { makeAuthenticatedApiCall } from '../apiHelper';

/**
 * Hook de lecture des feature flags par agence.
 *
 * - `useMyFeatureFlags` fait un seul fetch cache-shared (5min staleTime) pour tous les
 *   composants qui ont besoin de connaître les flags de l'agence de l'utilisateur.
 * - `useAgencyFeatureFlag(flagKey)` renvoie `{ enabled, isLoading }` pour un flag précis.
 */

export type FeatureFlagKey = 'form_cgia';

interface MyFlagsResponse {
  success: boolean;
  flags: Partial<Record<FeatureFlagKey, boolean>>;
}

const fetchMyFlags = async (): Promise<MyFlagsResponse> => {
  const response = await makeAuthenticatedApiCall<MyFlagsResponse>('get', '/api/feature-flags/me');
  return response.data;
};

export const useMyFeatureFlags = () =>
  useQuery({
    queryKey: ['myFeatureFlags'],
    queryFn: fetchMyFlags,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

export const useAgencyFeatureFlag = (
  flagKey: FeatureFlagKey,
): { enabled: boolean; isLoading: boolean } => {
  const { data, isLoading } = useMyFeatureFlags();
  return { enabled: Boolean(data?.flags?.[flagKey]), isLoading };
};
